import { mediaUrl } from '@bluheadless/ui-logic/src/assets'
import { useCountries } from '@bluheadless/ui-logic/src/hooks/useCountries'
import { useConfig } from '@bluheadless/ui-logic/src/providers/config'
import { cx } from '@emotion/css'
import { useTheme } from '@mui/system'
import { Grid, useMediaQuery } from '@mui/material'
import { array, number, shape, string, bool } from 'prop-types'
import { useRef } from 'react'
import { FormattedMessage } from 'react-intl'
import Typography from '../../../atoms/typography'
import GoogleMap from '../../google/maps/google-map'
import Marker from '../../google/maps/marker'
import { WorkingTimes as BHWorkingTimes } from '../list'
import { GetDirection, MapContainer, Title, WorkingTimes, WorkingTimesRow } from './store-locator-details.styled'

const StoreLocatorDetailsInfo = ({
	className,
	groupWorkingTimes = false,
	store: {
		title,
		latitude,
		longitude,
		street,
		postcode,
		city,
		countryCode,
		phone1,
		phone2,
		email,
		websiteUrl,
		region,
		workingTimes,
		additionalText,
		additionalText2,
	},
}) => {
	const theme = useTheme()
	const lgUp = useMediaQuery(theme.breakpoints.up('lg'))

	const {
		storelocator: {
			map: { options: mapOptions },
			marker,
		},
	} = useConfig()

	const { getRegionByCode } = useCountries()

	const mapRef = useRef()

	const mapConfigs = {
		defaultCenter: { lat: latitude, lng: longitude },
		defaultZoom: 18,
		...mapOptions,
	}

	const onGoogleApiLoaded = ({ map }) => {
		mapRef.current = map
	}

	return (
		<Grid className={cx('StoreLocatorDetailsInfo-root', className)} container spacing={0} rowSpacing={lgUp ? 8 : 6}>
			<Grid item xs={12}>
				{region && getRegionByCode(countryCode, region) && (
					<Typography color="grey.main">{getRegionByCode(countryCode, region)}</Typography>
				)}
				<Title variant="h4">{title}</Title>
				{street && <Typography color="grey.main">{street}</Typography>}
				{postcode && <Typography color="grey.main">{postcode}</Typography>}
				<Typography color="grey.main">
					{city && <Typography component="span">{city}</Typography>}
					{countryCode && <Typography component="span">{' (' + countryCode + ') '}</Typography>}
				</Typography>
				{phone1 && (
					<Typography color="grey.main" paragraph>
						{phone1}
					</Typography>
				)}
				{phone2 && (
					<Typography color="grey.main" paragraph>
						{phone2}
					</Typography>
				)}
				{email && <Typography color="grey.main">{email}</Typography>}
				{websiteUrl && <Typography color="grey.main">{websiteUrl}</Typography>}
				<GetDirection
					href={`https://www.google.com/maps/place/${latitude},${longitude}`}
					target="_blank"
					variant="text"
				>
					<FormattedMessage id="store_locator_get_direction" />
				</GetDirection>
				{additionalText?.length > 0 && <Typography color="grey.main">{additionalText}</Typography>}
				{additionalText2?.length > 0 && <Typography color="grey.main">{additionalText2}</Typography>}
			</Grid>
			{/* TODO: 🧑🏻‍💻 refactor */}
			{workingTimes && BHWorkingTimes({ workingTimes }) && (
				<WorkingTimesRow item xs={12}>
					<WorkingTimes workingTimes={workingTimes} groupWorkingTimes={groupWorkingTimes} />
				</WorkingTimesRow>
			)}
			<MapContainer item xs={12}>
				<GoogleMap mapMinHeight={300} onGoogleApiLoaded={onGoogleApiLoaded} {...mapConfigs}>
					<Marker
						className="marker-root"
						markerId={city}
						lat={latitude}
						lng={longitude}
						markerProps={marker.image ? { image: mediaUrl(`filoblu/storelocator/${marker.image}`) } : {}}
					/>
				</GoogleMap>
			</MapContainer>
		</Grid>
	)
}

StoreLocatorDetailsInfo.defaultProps = {}
StoreLocatorDetailsInfo.propTypes = {
	/**
	 * Defines className for root node.
	 */
	className: string,
	/**
	 * Show grouped working times.
	 */
	groupWorkingTimes: bool,
	store: shape({
		/**
		 * Name of the store.
		 */
		title: string.isRequired,
		/**
		 * Latitude of the store.
		 */
		latitude: number.isRequired,
		/**
		 * Longitude of the store.
		 */
		longitude: number.isRequired,
		/**
		 * Street of the store.
		 */
		street: string.isRequired,
		/**
		 * Postcode of the store.
		 */
		postcode: string,
		/**
		 * City of the store.
		 */
		city: string.isRequired,
		/**
		 * Country code of the store.
		 */
		countryCode: string.isRequired,
		/**
		 * Additional text of the store.
		 */
		additionalText: string,
		/**
		 * Alternative additional text of the store.
		 */
		additionalText2: string,
		/**
		 * Phone of the store.
		 */
		phone1: string,
		/**
		 * Email of the store.
		 */
		email: string,
		/**
		 * Website of the store.
		 */
		websiteUrl: string,
		/**
		 * Region of the store.
		 */
		region: string,
		/**
		 * Working times of the store.
		 */
		workingTimes: array,
	}).isRequired,
}

export default StoreLocatorDetailsInfo
